import React, { useEffect, useState } from 'react';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import FormField from '../../components/common/FormField';
import AddressFieldsArray, { emptyAddress, serializeAddressesForApi } from '../../components/common/AddressFieldsArray';
import { useToast } from '../../contexts/ToastContext';
import { adminService } from '../../services/adminService';
import { formatRole } from '../../utils/enums';

const ROLE_OPTIONS = [
  { value: 'client', label: formatRole('client') },
  { value: 'reseller', label: formatRole('reseller') },
  { value: 'store_owner', label: formatRole('store_owner') },
  { value: 'platform_admin', label: formatRole('platform_admin') }
];

// Roles que recebem pedidos físicos: endereço obrigatório no cadastro.
const ROLES_REQUIRING_ADDRESS = ['client', 'reseller'];

// Modal de criação / edição de usuário no painel admin.
// Em modo create, gera senha aleatória se não informada e dispara email de
// reset (decisão tomada pelo backend). Para client/reseller, exige ao menos
// um endereço inline (pode adicionar mais).
// CPF é editável apenas em create (read-only em edição).
const UserFormModal = ({ isOpen, onClose, user = null, onSaved }) => {
  const { error, success } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', cpf: '', phone: '',
    role: 'client', password: ''
  });
  const [addresses, setAddresses] = useState([]);

  const isEdit = !!user?.id;
  const requiresAddress = !isEdit && ROLES_REQUIRING_ADDRESS.includes(form.role);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        cpf: user.cpf || '',
        phone: user.phone || '',
        role: user.role || 'client',
        password: ''
      });
      setAddresses([]);
    } else {
      setForm({ name: '', email: '', cpf: '', phone: '', role: 'client', password: '' });
      // Pré-cria 1 endereço marcado como padrão pra UX direta.
      setAddresses([{ ...emptyAddress(), isDefault: true }]);
    }
  }, [user, isOpen]);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await adminService.updateUser(user.id, {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          role: form.role
        });
        success('Usuário atualizado');
      } else {
        const payload = {
          name: form.name,
          email: form.email,
          cpf: form.cpf || null,
          phone: form.phone || null,
          role: form.role
        };
        if (form.password) payload.password = form.password;
        if (ROLES_REQUIRING_ADDRESS.includes(form.role)) {
          if (!addresses.length) {
            error('Adicione pelo menos um endereço para clientes ou revendedores');
            setBusy(false);
            return;
          }
          payload.addresses = serializeAddressesForApi(addresses);
        } else if (addresses.length > 0 && addresses[0].zip) {
          // Roles sem obrigatoriedade ainda podem cadastrar endereço se preencheu.
          payload.addresses = serializeAddressesForApi(addresses);
        }
        const resp = await adminService.createUser(payload);
        if (resp.temporaryPasswordSent) {
          success('Usuário criado. Email de reset de senha enviado.');
        } else {
          success('Usuário criado com sucesso');
        }
      }
      onSaved?.();
      onClose();
    } catch (e) {
      error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Editar ${user?.name || 'usuário'}` : 'Novo usuário'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={busy}>{isEdit ? 'Salvar' : 'Criar usuário'}</Button>
        </>
      }
      size="lg"
    >
      <div className="space-y-3">
        <FormField label="Nome" required>
          <Input value={form.name} onChange={handle('name')} placeholder="Nome completo" />
        </FormField>
        <FormField label="Email" required>
          <Input type="email" value={form.email} onChange={handle('email')} placeholder="email@exemplo.com" />
        </FormField>
        <FormField label="CPF" helpText={isEdit ? 'Não pode ser alterado após o cadastro.' : 'Será normalizado (somente dígitos).'}>
          <Input value={form.cpf} onChange={handle('cpf')} disabled={isEdit} placeholder="000.000.000-00" />
        </FormField>
        <FormField label="Telefone">
          <Input value={form.phone} onChange={handle('phone')} placeholder="(11) 99999-9999" />
        </FormField>
        <FormField label="Papel" required>
          <Select value={form.role} onChange={handle('role')} options={ROLE_OPTIONS} />
        </FormField>
        {!isEdit && (
          <FormField label="Senha (opcional)" helpText="Se vazio, enviamos um email de reset.">
            <Input type="password" value={form.password} onChange={handle('password')} placeholder="••••••••" />
          </FormField>
        )}

        {!isEdit && (
          <div className="pt-2 border-t border-app-border">
            <h3 className="font-semibold text-app-text mb-1">
              Endereço{requiresAddress ? ' (obrigatório)' : ' (opcional)'}
            </h3>
            <p className="text-xs text-app-text-secondary mb-3">
              {requiresAddress
                ? 'Clientes e revendedores precisam de pelo menos um endereço. Você pode adicionar mais (entrega, cobrança).'
                : 'Endereços são opcionais para administradores e donos de loja.'}
            </p>
            <AddressFieldsArray
              addresses={addresses}
              onChange={setAddresses}
              required={requiresAddress}
              minItems={requiresAddress ? 1 : 0}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UserFormModal;
